import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { portalAuthApi } from "./api/auth";
import { PortalApiError, setPortalUnauthorizedHandler } from "./api/client";
import type { PortalMeDto } from "./api/types";

// External Intake & Collection v1.0 PR 4 — portal session context. Wholly
// separate from the staff useAuth(): different cookie scheme (Portal),
// different endpoint (/api/portal/auth/me), no permissions / role catalog.
// Recipients see only the packages their session is scoped to; brokers
// (PR 9) carry no package scope by default.

interface PortalAuthContextValue {
  me: PortalMeDto | null;
  loading: boolean;
  setMe: (next: PortalMeDto | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | undefined>(undefined);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<PortalMeDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await portalAuthApi.me();
      setMe(next);
    } catch (err) {
      // 401 just means no session yet (e.g., landing on /portal/r/:token
      // before redeem). Anything else is logged and treated the same — the
      // user lands on the redeem screen.
      if (!(err instanceof PortalApiError) || err.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("portal.me failed", err);
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Redirect to sign-in when a request 401s on a tab that believed it had a
  // session (e.g. the user signed out in another tab). A 401 with no known
  // session — the /auth/me probe on a public page — is left to routing.
  const meRef = useRef<PortalMeDto | null>(null);
  useEffect(() => {
    meRef.current = me;
  }, [me]);
  useEffect(() => {
    setPortalUnauthorizedHandler(() => {
      if (!meRef.current) return;
      meRef.current = null;
      setMe(null);
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/portal/login")
      ) {
        window.location.assign("/portal/login");
      }
    });
    return () => setPortalUnauthorizedHandler(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await portalAuthApi.logout();
    } catch {
      // Cookie may already be gone; treat as success.
    } finally {
      setMe(null);
    }
  }, []);

  const value = useMemo<PortalAuthContextValue>(
    () => ({ me, loading, setMe, refresh, logout }),
    [me, loading, refresh, logout],
  );
  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export function usePortalAuth(): PortalAuthContextValue {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used inside <PortalAuthProvider>.");
  return ctx;
}
